import React, { useState, useEffect, useMemo, useRef } from "react";

import "../App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";

import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { getEncoding, encodingForModel } from "js-tiktoken";

import { SSE } from "sse";
import { ResetAllButton } from "./ResetAllButton";
import { BudgetAllocationPrompt } from "./BudgetAllocationPrompt";

const TOKEN_LIMITS = {
  "gpt-5.4": 120000,
  "gpt-4o": 120000,
  "gpt-4": 8000,
  "gpt-4-turbo-preview": 120000,
  "gpt-3.5-turbo": 4050,
};

/** Mission System Analysis — default text inside `.gpt-rank-hint` (keep in sync with mission prompts). */
const GPT_RANK_HINT_PLACEHOLDER = "Type rank (1-4) and reasoning here...";

function stripGptRankHintClassIfEdited(root) {
  if (!root) return;
  root.querySelectorAll(".gpt-rank-hint").forEach((span) => {
    if (span.textContent !== GPT_RANK_HINT_PLACEHOLDER) {
      span.classList.remove("gpt-rank-hint");
    }
  });
}

const BUDGET_PLACEHOLDER_TEXT = "Enter";

function selectSpanContents(el) {
  if (!el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function stripBudgetPlaceholderClassIfEdited(root) {
  if (!root) return;
  root.querySelectorAll(".gpt-budget-placeholder").forEach((span) => {
    if (span.textContent !== BUDGET_PLACEHOLDER_TEXT) {
      span.classList.remove("gpt-budget-placeholder");
    }
  });
}

function escapeHtmlForFillInSlot(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stripFillInSlotIfEdited(root) {
  if (!root) return;
  root.querySelectorAll(".gpt-fill-in-slot").forEach((span) => {
    const enc = span.getAttribute("data-fill-in-encoded");
    if (!enc) return;
    try {
      const original = decodeURIComponent(enc);
      if (span.textContent !== original) {
        span.classList.remove("gpt-fill-in-slot");
        span.classList.remove("gpt-roi-fill-slot");
        span.removeAttribute("data-fill-in-encoded");
      }
    } catch {
      span.classList.remove("gpt-fill-in-slot");
      span.classList.remove("gpt-roi-fill-slot");
      span.removeAttribute("data-fill-in-encoded");
    }
  });
}

function SingleMessageDisplay({ message, gptName, couldBeVoiceOver, setVoiceOver, setElevatorPitch, couldBeElevatorPitch, voiceOverPromptIndex, elevatorPitchPromptIndex, prompts, scrollAnchorRef }) {
  const bgColor = message["role"] === "assistant" ? "#ebebeb" : "#007bff";
  const fgColor = message["role"] === "assistant" ? "#000000" : "white";
  const textSize = message["role"] === "assistant" ? "normal" : "large";
  const fontWeight = message["role"] === "user" ? "bold" : "normal";
  const borderStyle = message["role"] === "user" ? "2px solid #007bff" : "none";

  const messageRef = useRef(null);
  const [buttonLabel, setButtonLabel] = useState("Copy");

  const copyToClipboard = async () => {
    if (messageRef && messageRef.current) {
      try {
        if (typeof ClipboardItem !== "undefined") {
          const html = new Blob([messageRef.current.innerHTML], { type: "text/html" });
          const text = new Blob([message["content"]], { type: "text/plain" });
          const data = new ClipboardItem({ "text/html": html, "text/plain": text });
          await navigator.clipboard.write([data]);
        } else {
          const cb = (e) => {
            e.clipboardData.setData("text/html", messageRef.current.innerHTML);
            e.clipboardData.setData("text/plain", message["content"]);
            e.preventDefault();
          };
          document.addEventListener("copy", cb);
          document.execCommand("copy");
          document.removeEventListener("copy", cb);
        }
        setButtonLabel("Copied!");
        setTimeout(() => { setButtonLabel("Copy"); }, 1000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }
  };

  let this_message = <></>;
  if (message["role"] === "assistant") {
    if (message["content"] === "") {
      this_message = (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span style={{ marginLeft: "10px", color: "#666" }}>Generating response...</span>
        </div>
      );
    } else {
      this_message = (
        <div ref={messageRef}>
          <Markdown remarkPlugins={[remarkBreaks]}>{message["content"]}</Markdown>
        </div>
      );
    }
  } else {
    this_message = <span ref={messageRef}>{message["content"]}</span>;
  }

  let showAfterResult = <></>;
  if (message["showAfter"] !== undefined && message["showAfter"] >= 0 && prompts[message["showAfter"]]) {
    showAfterResult = prompts[message["showAfter"]].showAfter;
  }

  if (message["showAfter"] === voiceOverPromptIndex || couldBeVoiceOver) {
    setVoiceOver(message["content"]);
  }
  if (message["showAfter"] === elevatorPitchPromptIndex || couldBeElevatorPitch) {
    setElevatorPitch(message["content"]);
  }

  const outerStyle = {
    padding: "15px",
    backgroundColor: bgColor,
    color: fgColor,
    fontSize: textSize,
    fontWeight: fontWeight,
    border: borderStyle,
    position: "relative",
  };
  if (scrollAnchorRef) {
    outerStyle.scrollMarginTop = "max(100px, var(--sticky-header-height, 96px))";
  }

  return (
    <div ref={scrollAnchorRef || undefined} style={outerStyle}>
      {message["role"] === "user" && (
        <div style={{ position: "absolute", top: "5px", left: "10px", fontWeight: "bold", color: "#ddd", fontSize: "12px", fontStyle: "italic", zIndex: 1 }}>
          Prompt for {gptName}:
        </div>
      )}
      {message["role"] === "assistant" && (
        <div style={{ position: "absolute", top: "5px", left: "10px", fontWeight: "bold", color: "#999", fontSize: "12px", fontStyle: "italic", zIndex: 1 }}>
          {gptName} Output:
        </div>
      )}
      <div style={{ position: "relative", paddingBottom: message["role"] === "user" ? "10px" : "35px", paddingTop: message["role"] === "user" ? "15px" : "20px" }}>
        {this_message}
        {message["role"] === "assistant" && (
          <Button variant="outline-secondary" size="sm" style={{ position: "absolute", bottom: "-5px", right: "10px" }} onClick={copyToClipboard}>
            {buttonLabel}
          </Button>
        )}
      </div>
      {showAfterResult}
    </div>
  );
}

function MessagesDisplay({ messages, gptName, setVoiceOver, setElevatorPitch, voiceOverPromptIndex, elevatorPitchPromptIndex, prompts, lastSubmittedTurnRef }) {
  let counter = 0;
  let out = [];
  let nextIsVoiceOver = false;
  let nextIsElevatorPitch = false;

  if (messages.length === 0) {
    return (
      <div style={{ padding: "15px", backgroundColor: "#ebebeb", borderRadius: "5px" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span style={{ marginLeft: "10px", color: "#666" }}>Generating response...</span>
        </div>
      </div>
    );
  }

  const lastUserScrollIdx =
    messages.length >= 2 && messages[messages.length - 2].role === "user"
      ? messages.length - 2
      : -1;

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    out.push(
      <SingleMessageDisplay
        message={message}
        key={counter}
        gptName={gptName}
        couldBeVoiceOver={nextIsVoiceOver}
        setVoiceOver={setVoiceOver}
        setElevatorPitch={setElevatorPitch}
        couldBeElevatorPitch={nextIsElevatorPitch}
        voiceOverPromptIndex={voiceOverPromptIndex}
        elevatorPitchPromptIndex={elevatorPitchPromptIndex}
        prompts={prompts}
        scrollAnchorRef={lastSubmittedTurnRef && i === lastUserScrollIdx ? lastSubmittedTurnRef : undefined}
      />
    );
    if (message["role"] === "user" && message["content"].includes("voice-over monologue")) {
      nextIsVoiceOver = true;
    } else {
      nextIsVoiceOver = false;
    }
    if (message["role"] === "user" && message["content"].includes("elevator pitch for the film")) {
      nextIsElevatorPitch = true;
    } else {
      nextIsElevatorPitch = false;
    }
    counter += 1;
  }

  return <div><div>{out}</div></div>;
}

function Gpt(props) {
  const [messages, setMessages] = React.useState([]);
  const [hasStarted, setHasStarted] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [promptNum, setPromptNum] = React.useState(0);
  const [lastPromptRun, setLastPromptRun] = React.useState(-1);
  const [showAfter, setShowAfter] = React.useState(-1);
  const [showScroll, setShowScroll] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [usedPrompts, setUsedPrompts] = React.useState(new Set());
  const [clearConfirm, setClearConfirm] = React.useState(false);
  const [hasVisitedVoiceAfterCapcom, setHasVisitedVoiceAfterCapcom] = React.useState(false);

  const btConfig = props.workshopConfig.budgetTable;
  const [budgetTableVisible, setBudgetTableVisible] = useState(() => {
    if (!btConfig) return false;
    try {
      const saved = localStorage.getItem(btConfig.storageKey);
      return saved ? true : false;
    } catch { return false; }
  });
  const [budgetValues, setBudgetValues] = useState(() => {
    if (!btConfig) return {};
    try {
      const saved = localStorage.getItem(btConfig.storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [aiBudgetValues, setAiBudgetValues] = useState(() => {
    if (!btConfig) return {};
    try {
      const saved = localStorage.getItem(btConfig.storageKey + "-ai");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [returnValues, setReturnValues] = useState(() => {
    if (!btConfig) return {};
    try {
      const saved = localStorage.getItem(btConfig.storageKey + "-return");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [roiValues, setRoiValues] = useState(() => {
    if (!btConfig) return {};
    try {
      const saved = localStorage.getItem(btConfig.storageKey + "-roi");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const budgetFieldsIncomplete = false;

  const budgetAllFilled = btConfig && btConfig.systems.every(s => budgetValues[s]?.trim());

  const budgetTableElement = btConfig && budgetTableVisible ? (() => {
    const thStyle = { padding: "1px 8px", textAlign: "left", fontSize: "0.65em", color: "#888", fontWeight: "600", borderBottom: "1px solid #eee" };
    const formatDollars = (v) => {
      const digits = (v || "").replace(/[^0-9]/g, "");
      if (!digits) return "";
      return "$" + Number(digits).toLocaleString("en-US");
    };
    return (
      <div>
        <p style={{ margin: "0 0 6px 0", fontSize: "0.9em" }}>
          Enter the cost of each system in the table before proceeding.
        </p>
        <div style={{
          margin: "10px auto 10px auto",
          maxWidth: "340px",
          border: "2px solid #f05f40",
          borderRadius: "6px",
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(240, 95, 64, 0.15)",
        }}>
          <div style={{
            background: "linear-gradient(135deg, #f05f40, #ff7e5f)",
            padding: "2px 10px",
            textAlign: "center",
          }}>
            <span style={{
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.7em",
              letterSpacing: "0.5px",
            }}>{btConfig.title}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f4e6d6" }}>
                <th style={{ ...thStyle, width: "45%" }}>System</th>
                <th style={thStyle}>Cost</th>
              </tr>
            </thead>
          <tbody>
            {btConfig.systems.map((system, idx) => {
              const rawVal = budgetValues[system] || "";
              const filled = !!rawVal.trim();
              return (
                <tr key={system} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fdf6f2" }}>
                  <td style={{ padding: "1px 8px", fontWeight: "500", color: "#444", borderBottom: "1px solid #f0ebe6", fontSize: "0.7em" }}>{system}</td>
                  <td style={{ padding: "1px 8px", borderBottom: "1px solid #f0ebe6" }}>
                    <input
                      type="text"
                      value={formatDollars(rawVal)}
                      placeholder="$0"
                      onChange={(e) => {
                        const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
                        const digits = e.target.value.replace(/[^0-9]/g, "");
                        const updated = { ...budgetValues, [system]: digits };
                        setBudgetValues(updated);
                        try { localStorage.setItem(btConfig.storageKey, JSON.stringify(updated)); } catch {}
                        requestAnimationFrame(() => { window.scrollTo(0, scrollPos); });
                      }}
                      style={{
                        width: "100%",
                        padding: "1px 4px",
                        border: filled ? "1.5px solid #ddd" : "2px solid #007bff",
                        borderRadius: "4px",
                        fontSize: "0.7em",
                        color: "#333",
                        outline: "none",
                        transition: "border-color 0.2s",
                        backgroundColor: filled ? "#fafafa" : "#f0f7ff",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#f05f40"; e.target.style.backgroundColor = "#fff"; }}
                      onBlur={(e) => {
                        const isFilled = !!e.target.value.trim();
                        e.target.style.borderColor = isFilled ? "#ddd" : "#007bff";
                        e.target.style.backgroundColor = isFilled ? "#fafafa" : "#f0f7ff";
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#f4e6d6" }}>
              <td style={{ padding: "2px 8px", fontWeight: "bold", color: "#444", fontSize: "0.7em", borderTop: "2px solid #f05f40" }}>Total</td>
              <td style={{ padding: "2px 8px", fontWeight: "bold", color: "#333", fontSize: "0.7em", borderTop: "2px solid #f05f40" }}>
                {(() => {
                  const total = btConfig.systems.reduce((sum, s) => {
                    const digits = (budgetValues[s] || "").replace(/[^0-9]/g, "");
                    return sum + (digits ? Number(digits) : 0);
                  }, 0);
                  return total > 0 ? "$" + total.toLocaleString("en-US") : "$0";
                })()}
              </td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>
    );
  })() : null;

  const roiAllFilled = btConfig && btConfig.systems.every(s => returnValues[s]?.trim() && roiValues[s] && roiValues[s] !== "");

  const roiTableElement = btConfig && budgetTableVisible ? (() => {
    const thStyle = { padding: "1px 8px", textAlign: "left", fontSize: "0.65em", color: "#888", fontWeight: "600", borderBottom: "1px solid #eee" };
    const formatDollars = (v) => {
      const digits = (v || "").replace(/[^0-9]/g, "");
      if (!digits) return "";
      return "$" + Number(digits).toLocaleString("en-US");
    };
    return (
      <div>
        <p style={{ margin: "0 0 6px 0", fontSize: "0.9em" }}>
          Enter the return and ROI for each system in the table before proceeding.
        </p>
        <div style={{
          margin: "10px auto 10px auto",
          maxWidth: "480px",
          border: "2px solid #f05f40",
          borderRadius: "6px",
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(240, 95, 64, 0.15)",
        }}>
          <div style={{
            background: "linear-gradient(135deg, #f05f40, #ff7e5f)",
            padding: "2px 10px",
            textAlign: "center",
          }}>
            <span style={{
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.7em",
              letterSpacing: "0.5px",
            }}>{btConfig.title}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f4e6d6" }}>
                <th style={{ ...thStyle, width: "28%" }}>System</th>
                <th style={{ ...thStyle, width: "24%" }}>Cost</th>
                <th style={{ ...thStyle, width: "24%" }}>Return</th>
                <th style={{ ...thStyle, width: "24%" }}>ROI</th>
              </tr>
            </thead>
            <tbody>
              {btConfig.systems.map((system, idx) => {
                const costVal = budgetValues[system] || "";
                const retVal = returnValues[system] || "";
                const retFilled = !!retVal.trim();
                const roiVal = roiValues[system] || "";
                const roiFilled = !!roiVal;
                return (
                  <tr key={system} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fdf6f2" }}>
                    <td style={{ padding: "1px 8px", fontWeight: "500", color: "#444", borderBottom: "1px solid #f0ebe6", fontSize: "0.7em" }}>{system}</td>
                    <td style={{ padding: "1px 8px", borderBottom: "1px solid #f0ebe6", fontSize: "0.7em", color: "#666" }}>
                      {formatDollars(costVal) || "$0"}
                    </td>
                    <td style={{ padding: "1px 8px", borderBottom: "1px solid #f0ebe6" }}>
                      <input
                        type="text"
                        value={formatDollars(retVal)}
                        placeholder="$0"
                        onChange={(e) => {
                          const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
                          const digits = e.target.value.replace(/[^0-9]/g, "");
                          const updated = { ...returnValues, [system]: digits };
                          setReturnValues(updated);
                          try { localStorage.setItem(btConfig.storageKey + "-return", JSON.stringify(updated)); } catch {}
                          requestAnimationFrame(() => { window.scrollTo(0, scrollPos); });
                        }}
                        style={{
                          width: "100%",
                          padding: "1px 4px",
                          border: retFilled ? "1.5px solid #ddd" : "2px solid #007bff",
                          borderRadius: "4px",
                          fontSize: "0.7em",
                          color: "#333",
                          outline: "none",
                          transition: "border-color 0.2s",
                          backgroundColor: retFilled ? "#fafafa" : "#f0f7ff",
                        }}
                        onFocus={(e) => { e.target.style.borderColor = "#f05f40"; e.target.style.backgroundColor = "#fff"; }}
                        onBlur={(e) => {
                          const isFilled = !!e.target.value.trim();
                          e.target.style.borderColor = isFilled ? "#ddd" : "#007bff";
                          e.target.style.backgroundColor = isFilled ? "#fafafa" : "#f0f7ff";
                        }}
                      />
                    </td>
                    <td style={{ padding: "1px 8px", borderBottom: "1px solid #f0ebe6" }}>
                      <select
                        value={roiVal}
                        onChange={(e) => {
                          const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
                          const updated = { ...roiValues, [system]: e.target.value };
                          setRoiValues(updated);
                          try { localStorage.setItem(btConfig.storageKey + "-roi", JSON.stringify(updated)); } catch {}
                          requestAnimationFrame(() => { window.scrollTo(0, scrollPos); });
                        }}
                        style={{
                          width: "100%",
                          padding: "1px 4px",
                          border: roiFilled ? "1.5px solid #ddd" : "2px solid #007bff",
                          borderRadius: "4px",
                          fontSize: "0.7em",
                          color: roiVal ? "#333" : "#999",
                          outline: "none",
                          transition: "border-color 0.2s",
                          backgroundColor: roiFilled ? "#fafafa" : "#f0f7ff",
                          cursor: "pointer",
                        }}
                        onFocus={(e) => { e.target.style.borderColor = "#f05f40"; e.target.style.backgroundColor = "#fff"; }}
                        onBlur={(e) => {
                          const isFilled = !!e.target.value;
                          e.target.style.borderColor = isFilled ? "#ddd" : "#007bff";
                          e.target.style.backgroundColor = isFilled ? "#fafafa" : "#f0f7ff";
                        }}
                      >
                        <option value="">None</option>
                        <option value="Low">Low</option>
                        <option value="Moderate">Moderate</option>
                        <option value="High">High</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: "#f4e6d6" }}>
                <td style={{ padding: "2px 8px", fontWeight: "bold", color: "#444", fontSize: "0.7em", borderTop: "2px solid #f05f40" }}>Total</td>
                <td style={{ padding: "2px 8px", fontWeight: "bold", color: "#333", fontSize: "0.7em", borderTop: "2px solid #f05f40" }}>
                  {(() => {
                    const total = btConfig.systems.reduce((sum, s) => {
                      const digits = (budgetValues[s] || "").replace(/[^0-9]/g, "");
                      return sum + (digits ? Number(digits) : 0);
                    }, 0);
                    return total > 0 ? "$" + total.toLocaleString("en-US") : "$0";
                  })()}
                </td>
                <td style={{ padding: "2px 8px", fontWeight: "bold", color: "#333", fontSize: "0.7em", borderTop: "2px solid #f05f40" }}>
                  {(() => {
                    const total = btConfig.systems.reduce((sum, s) => {
                      const digits = (returnValues[s] || "").replace(/[^0-9]/g, "");
                      return sum + (digits ? Number(digits) : 0);
                    }, 0);
                    return total > 0 ? "$" + total.toLocaleString("en-US") : "$0";
                  })()}
                </td>
                <td style={{ padding: "2px 8px", borderTop: "2px solid #f05f40" }}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  })() : null;

  const prompts = props.workshopConfig.getPrompts({ handlePromptButtonClicked, setActiveTab: props.setActiveTab, missionData: props.missionData, budgetTableElement, budgetAllFilled, roiTableElement, roiAllFilled });

  const checkScrollTop = () => {
    const scrollPosition = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const isScrollable = documentHeight > windowHeight + 100;
    const shouldShow = isScrollable && (scrollPosition > 200 || scrollPosition + windowHeight < documentHeight - 200);

    setShowScroll(shouldShow);
    setShowScrollBottom(shouldShow);
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    window.addEventListener("scroll", checkScrollTop);
    // return () => window.removeEventListener("scroll", checkScrollTop);
  }, []);

  useEffect(() => {
    if (props.activeTab === "gpt") {
      if (messages?.length < 1) {
        handlePromptButtonClicked(null, 0);

        if (!hasStarted) {
          const savedMessages = localStorage.getItem(props.storageKey);

          if (savedMessages) {
            try {
              let parsed = JSON.parse(savedMessages);
              if (parsed) {
                console.log(parsed);
                setMessages(parsed);
                setHasStarted(true);

                const restoredUsed = new Set();
                let maxShowAfter = -1;
                parsed.forEach(msg => {
                  if (msg.showAfter !== undefined && msg.showAfter >= 0) {
                    restoredUsed.add(msg.showAfter);
                    if (msg.showAfter > maxShowAfter) {
                      maxShowAfter = msg.showAfter;
                    }
                  }
                });
                setUsedPrompts(restoredUsed);
                setLastPromptRun(maxShowAfter);

                const restorePosterThreshold = props.workshopConfig.posterModeThreshold;
                if (restorePosterThreshold != null && maxShowAfter >= restorePosterThreshold) {
                  props.setPosterModeUnlocked?.(true);
                  if (props.setDalleSourcePromptIndex) {
                    props.setDalleSourcePromptIndex(maxShowAfter);
                  }
                }

                const nextPrompt = maxShowAfter + 1;
                if (nextPrompt < prompts.length) {
                  handlePromptButtonClicked(null, nextPrompt);
                } else if (maxShowAfter >= 0) {
                  handlePromptButtonClicked(null, maxShowAfter);
                }
              }
            } catch {
              console.log("Failed to parse saved messages.");
            }
          }
        }
      } else {
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      }
    }
  }, [props.activeTab]);

  useEffect(() => {
    if (props.activeTab === "voice-cloning" && lastPromptRun >= 2) {
      setHasVisitedVoiceAfterCapcom(true);
    }
  }, [props.activeTab, lastPromptRun]);

  useEffect(() => {
    if (props.activeTab === "gpt" && props.pendingGptPrompt != null) {
      const idx = props.pendingGptPrompt;
      props.setPendingGptPrompt(null);
      handlePromptButtonClicked(null, idx);
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100);
    }
  }, [props.activeTab, props.pendingGptPrompt]);

  useEffect(() => {
    if (btConfig && usedPrompts.has(btConfig.triggerPromptIndex)) {
      setBudgetTableVisible(true);
      if (Object.keys(budgetValues).length === 0) {
        const initial = {};
        btConfig.systems.forEach(s => { initial[s] = ""; });
        setBudgetValues(initial);
        try { localStorage.setItem(btConfig.storageKey, JSON.stringify(initial)); } catch {}
      }
    }
  }, [usedPrompts]);


  const requestId = React.useRef(0);
  const questionTextRef = useRef(null);
  const budgetAllocationRef = useRef(null);
  const [budgetPromptKey, setBudgetPromptKey] = useState(0);
  /** Scroll target: user bubble for the latest Go submission (prompt + GPT reply below). */
  const lastSubmittedTurnRef = useRef(null);

  let currentStreamId = useRef(null);
  let ignoreStreamId = useRef(null);

  let spinnerDisp = "";

  let gptName = "GPT";
  if (props.gptModel.includes("gpt-5")) {
    gptName = "GPT5";
  } else if (props.gptModel.includes("gpt-4")) {
    gptName = "GPT4";
  } else if (props.gptModel.includes("gpt-3.5")) {
    gptName = "GPT3.5";
  }

  let mainSummary = <></>;
  if (messages !== undefined && messages.length > 0) {
    mainSummary = (
      <MessagesDisplay
        messages={messages}
        gptName={gptName}
        setVoiceOver={props.setVoiceOver}
        setElevatorPitch={props.setElevatorPitch}
        voiceOverPromptIndex={props.workshopConfig.voiceOverPromptIndex ?? 3}
        elevatorPitchPromptIndex={props.workshopConfig.elevatorPitchPromptIndex ?? 4}
        prompts={prompts}
        lastSubmittedTurnRef={props.workshopConfig.scrollOutputIntoViewOnGo ? lastSubmittedTurnRef : undefined}
      />
    );
    spinnerDisp = "none";
  }

  let chatBoxTopMargin = hasStarted ? "20px" : "";

  let respDisp = hasStarted ? "" : "none";
  const stopVis = isStreaming ? "visible" : "hidden";

  return (
    <Container className="mt-1 mb-0">
      <Row className="mb-0">
        <Col>
          <Card>
            <Card.Header
              as="h4"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f4e6d6",
              }}
            >
              Chat with {gptName}
              {messages?.length > 1 && (
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <Button size="sm" className="btn-clear-text" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={clearConfirm ? "danger" : "outline-secondary"} onClick={handleClearClick}>
                    {clearConfirm ? "Confirm Clear" : "Clear All Text"}
                  </Button>
                </div>
              )}
            </Card.Header>

            <Card.Body style={{ backgroundColor: "#fffaf6" }}>
              <div style={{
                backgroundColor: "white",
                border: "2px solid #f05f40",
                borderRadius: "8px",
                padding: "10px 15px 15px 15px",
                margin: "10px 0",
                boxShadow: "0 0 10px rgba(240, 95, 64, 0.3)"
              }}>
                <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                  Instructions:
                </h3>
                <h4 style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0 0 5px 0" }}>
                  {props.workshopConfig.instructionTitle}
                </h4>
                <p style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0" }}>
                  {props.workshopConfig.instructionSubtitle}
                </p>
              </div>

              <section
                className="response-section"
                style={{ display: respDisp }}
              >
                {mainSummary}
              </section>
              <section
                className="input-section"
                style={{ marginTop: chatBoxTopMargin }}
              >
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div
                    style={{
                      margin: "10px",
                      marginBottom: props.workshopConfig.promptButtonsSectionMarginBottom ?? "30px",
                    }}
                  >
                    {generatePromptButtons()}
                    {generateHelpText()}
                  </div>
                  <hr style={{ marginTop: props.workshopConfig.promptDividerMarginTop ?? "40px" }} />
                  {props.workshopConfig.doublePromptDivider && (
                    <hr style={{ marginTop: "0px", marginBottom: "10px" }} />
                  )}
                  <div style={{ position: "relative" }}>
                    <div style={{ 
                      position: "absolute", 
                      top: "8px", 
                      left: "50px", 
                      color: "#999", 
                      fontWeight: "bold", 
                      fontSize: "12px",
                      fontStyle: "italic",
                      zIndex: 10,
                      pointerEvents: "none"
                    }}>
                      Prompt for {gptName}:
                    </div>
                    {promptNum >= 0 && prompts[promptNum]?.budgetAllocation ? (
                      <BudgetAllocationPrompt
                        key={budgetPromptKey}
                        ref={budgetAllocationRef}
                        missionName={props.missionData?.name || "Unknown Mission"}
                      />
                    ) : (
                      <div
                        onInput={checkIfEmpty}
                        onMouseDown={handleBudgetPlaceholderMouseDown}
                        onMouseUp={handleFillInSlotMouseUp}
                        onKeyUp={handleFillInSlotKeyUp}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onSubmit(e);
                          }
                        }}
                        className="form-control orange-highlight"
                        contentEditable={!isStreaming}
                        ref={questionTextRef}
                        data-placeholder={
                          "Type your message to " + gptName + "..."
                        }
                        style={{ minHeight: "6.5em", position: "relative", width: "100%" }}
                      >
                      </div>
                    )}
                    <Button
                      variant="primary"
                      type="button"
                      className="btn-gpt-go"
                      disabled={isStreaming || budgetFieldsIncomplete}
                      style={budgetFieldsIncomplete ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                      onClick={onSubmit}
                    >
                      Go
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-gpt-stop"
                      style={{
                        visibility: stopVis,
                      }}
                      onClick={handleStopButton}
                    >
                      Stop
                    </Button>
                  </div>
                </Form>

              </section>
            </Card.Body>
            <Card.Footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "0.4rem 0.75rem",
                marginBottom: "0",
                borderBottom: "none",
                backgroundColor: "#f4e6d6",
              }}
            >
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                {messages?.length > 1 && (
                  <Button size="sm" className="btn-clear-text" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }} variant={clearConfirm ? "danger" : "outline-secondary"} onClick={handleClearClick}>
                    {clearConfirm ? "Confirm Clear" : "Clear All Text"}
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
          <Button
            className="scroll-arrow"
            onClick={scrollTop}
            style={{
              display: showScroll ? "flex" : "none",
              position: "fixed",
              bottom: "80px",
              right: "20px",
              zIndex: "1000",
            }}
            variant="secondary"
          >
            ↑
          </Button>
          <Button
            className="scroll-arrow"
            onClick={scrollBottom}
            style={{
              display: showScrollBottom ? "flex" : "none",
              position: "fixed",
              bottom: "20px",
              right: "20px",
              zIndex: "1000",
            }}
            variant="secondary"
          >
            ↓
          </Button>
        </Col>
      </Row>
    </Container>
  );

  function generateHelpText() {
    if (prompts[promptNum] && "helpText in prompts[promptNum]") {
      return <>{prompts[promptNum]["helpText"]}</>;
    }
    return <></>;
  }

  function generatePromptButtons() {
    const primaryCount = props.workshopConfig.primaryPromptCount || 6;
    const compact = primaryCount > 6;
    const snug = primaryCount >= 6;
    const baseButtonStyle = { 
      marginRight: "0px", 
      marginBottom: "0px",
      whiteSpace: "normal",
      flexShrink: 1,
      flexGrow: 1,
      flexBasis: compact ? "auto" : 0,
      minWidth: compact ? "fit-content" : 0,
      overflow: compact ? "visible" : "hidden",
      fontSize: compact ? "0.92em" : snug ? "0.82em" : undefined,
      transition: "all 0.3s ease",
      height: compact ? "3.6em" : undefined,
      display: compact ? "inline-flex" : undefined,
      alignItems: compact ? "center" : undefined,
      justifyContent: compact ? "center" : undefined,
      padding: compact ? "1px 6px" : snug ? "4px 6px" : undefined,
      lineHeight: compact ? "1.1" : snug ? "1.2" : undefined,
      textAlign: compact ? "center" : undefined,
      backgroundColor: "white",
    };
    
    const usedButtonStyle = {
      ...baseButtonStyle,
      backgroundColor: "#f0f7ff",
      borderColor: "#007bff",
      color: "#007bff"
    };
    
    let primaryButtons = [];
    let advancedButtons = [];

    const revealAfterIdx = props.workshopConfig.revealAfterPromptIndex;
    const laterButtonsRevealed = revealAfterIdx == null || usedPrompts.has(revealAfterIdx);
    
    for (let i = 0; i < prompts.length; i++) {
      if (i < primaryCount && revealAfterIdx != null && !laterButtonsRevealed && i > revealAfterIdx) {
        continue;
      }
      const isUsed = usedPrompts.has(i);
      const isLocked = compact && i < primaryCount && i > 0 && !usedPrompts.has(i - 1);
      const isGrayed = i > 0 && i < primaryCount && !usedPrompts.has(0);
      let variant = "outline-secondary";
      let btnClass = "prompt-btn";
      let buttonStyle = isUsed ? { ...usedButtonStyle } : { ...baseButtonStyle };
      if (isUsed) {
        btnClass = "prompt-btn prompt-btn-used";
      }
      if (isGrayed && !isUsed) {
        buttonStyle = { ...buttonStyle, backgroundColor: "#e0e0e0", color: "#999", borderColor: "#ccc" };
        btnClass = "prompt-btn prompt-btn-grayed";
      }
      if (isLocked) {
        buttonStyle = { ...buttonStyle, opacity: 0.45 };
      }
      
      if (promptNum == i) {
        variant = "outline-secondary";
        btnClass = "prompt-btn prompt-btn-selected";
        buttonStyle = {
          ...baseButtonStyle,
          backgroundColor: "white",
          fontSize: compact ? "0.98em" : snug ? "0.92em" : "1.2em",
          fontWeight: "bold",
          border: "2px solid #007bff",
          boxShadow: "0 0 5px rgba(0, 123, 255, 0.3)"
        };
      }
      
      if (lastPromptRun + 1 == i && (i < 3 || (i === 3 && hasVisitedVoiceAfterCapcom)) && !isStreaming && promptNum !== i && !prompts[lastPromptRun]?.noAutoAdvance) {
        btnClass = "animated-outline";
      }
      
      const cornerNumber = i < primaryCount ? (
        <span style={{
          position: "absolute",
          top: "2px",
          left: "4px",
          fontWeight: "bold",
          opacity: 0.4,
          fontSize: "0.6em",
          lineHeight: 1,
        }}>{i + 1}</span>
      ) : null;

      const button = (
        <Button
          variant={variant}
          style={{ ...buttonStyle, position: i < primaryCount ? "relative" : undefined }}
          key={i}
          onClick={(e) => handlePromptButtonClicked(e, i)}
          className={btnClass}
        >
          {cornerNumber}
          {prompts[i]["title"]}
        </Button>
      );
      
      if (i < primaryCount) {
        primaryButtons.push(button);
      } else {
        advancedButtons.push(button);
      }
    }
    
    const breakAfter = props.workshopConfig.primaryRowBreakAfter;
    const primaryRowStyle = {
      display: "flex",
      overflowX: compact ? "hidden" : "hidden",
      flexWrap: compact ? "wrap" : "nowrap",
      paddingBottom: "10px",
      gap: compact ? "5px" : snug ? "6px" : "10px",
      scrollbarWidth: "thin",
      scrollbarColor: "#ccc #f1f1f1",
      alignItems: "stretch"
    };
    const secondPrimaryRowStyle = {
      ...primaryRowStyle,
      paddingBottom: props.workshopConfig.primarySecondRowPaddingBottom ?? primaryRowStyle.paddingBottom,
    };

    return (
      <div>
        {breakAfter && breakAfter < primaryButtons.length ? (
          <>
            <div style={primaryRowStyle}>
              {primaryButtons.slice(0, breakAfter)}
            </div>
            <div style={secondPrimaryRowStyle}>
              {primaryButtons.slice(breakAfter)}
            </div>
          </>
        ) : (
          <div style={primaryRowStyle}>
            {primaryButtons}
          </div>
        )}
        
        {/* Only show buttons 7-17 after image generation starts */}
        {props.imageGenerationStarted && !props.workshopConfig.hideAdvancedButtons && (
          <>
            <hr style={{
              border: "none",
              borderTop: "1px solid #d3d3d3",
              margin: "5px 0 10px 0"
            }} />
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              paddingBottom: "10px",
              gap: "10px",
              alignItems: "center"
            }}>
              {props.workshopConfig.advancedBreakAfter
                ? advancedButtons.slice(0, props.workshopConfig.advancedBreakAfter)
                : advancedButtons}
            </div>
            {props.workshopConfig.advancedBreakAfter && advancedButtons.length > props.workshopConfig.advancedBreakAfter && (
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                paddingBottom: "10px",
                gap: "10px",
                alignItems: "center"
              }}>
                {advancedButtons.slice(props.workshopConfig.advancedBreakAfter)}
              </div>
            )}
          </>
        )}
        
        {(props.videoGenerationStarted || props.readyForPoster) && props.workshopConfig.postVideoPrompts && (
          <>
            <hr style={{
              border: "none",
              borderTop: "1px solid #d3d3d3",
              margin: "5px 0 10px 0"
            }} />
            <div className="post-video-prompts" style={{
              display: "flex",
              flexWrap: "nowrap",
              paddingBottom: "10px",
              gap: "10px",
              alignItems: "center"
            }}>
              {props.workshopConfig.postVideoPrompts.map((pvp, idx) => {
                const pvpIndex = prompts.length + idx;
                const isPvpSelected = promptNum === pvpIndex;
                const isPvpUsed = usedPrompts.has(pvpIndex);
                let pvpClass = "prompt-btn";
                let pvpStyle = { ...baseButtonStyle };
                if (isPvpUsed) {
                  pvpStyle = { ...usedButtonStyle };
                  pvpClass = "prompt-btn prompt-btn-used";
                }
                if (isPvpSelected) {
                  pvpClass = "prompt-btn prompt-btn-selected";
                  pvpStyle = {
                    ...baseButtonStyle,
                    backgroundColor: "white",
                    fontSize: "1.2em",
                    fontWeight: "bold",
                    border: "2px solid #007bff",
                    boxShadow: "0 0 5px rgba(0, 123, 255, 0.3)"
                  };
                }
                return (
                  <Button
                    variant="outline-secondary"
                    style={{ ...pvpStyle, position: "relative", flexGrow: 1, flexBasis: 0 }}
                    key={"pvp-" + idx}
                    onClick={(e) => {
                      setPromptNum(pvpIndex);
                      if (questionTextRef.current) {
                        if (pvp.customPlaceholder) {
                          questionTextRef.current.innerHTML = "";
                          questionTextRef.current.setAttribute("data-placeholder", pvp.customPlaceholder);
                        } else {
                          questionTextRef.current.innerHTML = pvp.promptVal[0];
                          questionTextRef.current.setAttribute("data-placeholder", "Type your message to " + gptName + "...");
                        }
                      }
                      setTimeout(() => {
                        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                      }, 100);
                    }}
                    className={pvpClass}
                  >
                    {pvp.title}
                  </Button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  function handlePromptButtonClicked(e, index) {
    if (index < 0 || index >= prompts.length) {
      console.error("Invalid index");
      return;
    }

    setPromptNum(index);

    if (prompts[index]["budgetAllocation"]) {
      if (questionTextRef.current) {
        questionTextRef.current.innerHTML = "";
        questionTextRef.current.setAttribute("data-placeholder", "Type your message to " + gptName + "...");
      }
      setBudgetPromptKey((k) => k + 1);
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      }, 100);
      return;
    }

    /** After leaving Mission Budget, the contenteditable is not mounted until the next commit — ref is null synchronously. */
    let applyAttempts = 0;
    const applyNonBudgetPromptToEditor = () => {
      const el = questionTextRef.current;
      if (!el) {
        if (++applyAttempts > 120) {
          console.warn("GPT prompt editor ref not available after prompt switch");
          return;
        }
        requestAnimationFrame(applyNonBudgetPromptToEditor);
        return;
      }

      let htmlContent = "";
      let fillInPosition;

      const filmIdeas = props.workshopConfig.filmIdeas;
      const randomNumber = Math.floor(Math.random() * filmIdeas.length);

      let fillInStr = prompts[index]["fillIn"];
      if (index == 0) {
        fillInStr = filmIdeas[randomNumber];
      }

      const values = prompts[index]["promptVal"];
      if (values.length === 2 && prompts[index].wrapFillInSlot) {
        const esc = escapeHtmlForFillInSlot(fillInStr);
        const enc = encodeURIComponent(fillInStr);
        htmlContent = `${values[0]}<span class="gpt-fill-in-slot" data-fill-in-encoded="${enc}">${esc}</span>${values[1]}`;
      } else if (values.length === 2) {
        fillInPosition = values[0].length;
        htmlContent = `${values[0]}${fillInStr}${values[1]}`;
      } else {
        htmlContent = values[0];
      }

      if (prompts[index]["customPlaceholder"]) {
        el.innerHTML = "";
        el.setAttribute("data-placeholder", prompts[index]["customPlaceholder"]);
      } else {
        el.innerHTML = htmlContent;
        el.setAttribute("data-placeholder", "Type your message to " + gptName + "...");
        if (el.querySelector(".gpt-fill-in-slot")) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const slot = questionTextRef.current?.querySelector(".gpt-fill-in-slot");
              if (slot) selectSpanContents(slot);
            });
          });
        } else if (fillInPosition !== undefined) {
          highlightText(fillInPosition, fillInStr.length);
        }
        if (el.querySelector(".gpt-budget-placeholder")) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const first = questionTextRef.current?.querySelector(".gpt-budget-placeholder");
              if (first) selectSpanContents(first);
            });
          });
        }
      }

      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      }, 100);
    };

    applyNonBudgetPromptToEditor();
  }

  function highlightText(startPosition, length) {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStart(questionTextRef.current.firstChild, startPosition);
    range.setEnd(questionTextRef.current.firstChild, startPosition + length);

    selection.removeAllRanges();
    selection.addRange(range);
  }

  // function highlightText(startPosition, length) {
  //   const range = document.createRange();
  //   const selection = window.getSelection();
  //   const node = questionTextRef.current.childNodes[0]; // Assuming the first child is the text node, this might need adjustment

  //   try {
  //     range.setStart(node, startPosition);
  //     range.setEnd(node, startPosition + length);
  //     selection.removeAllRanges();
  //     selection.addRange(range);
  //   } catch (error) {
  //     console.error('Error highlighting text:', error);
  //   }
  // }

  function saveMesssagesToLocalStorage(newMessages) {
    try {
      localStorage.setItem(props.storageKey, JSON.stringify(newMessages));
    } catch (e) {
      console.error("An error occurred while saving to Local Storage", e);
    }
  }

  function checkIfEmpty() {
    stripGptRankHintClassIfEdited(questionTextRef.current);
    stripBudgetPlaceholderClassIfEdited(questionTextRef.current);
    stripFillInSlotIfEdited(questionTextRef.current);
    if (questionTextRef.current.innerHTML === "<br>") {
      questionTextRef.current.innerHTML = "";
    }
  }

  function handleBudgetPlaceholderMouseDown(e) {
    const span = e.target.closest?.(".gpt-budget-placeholder");
    if (!span) return;
    e.preventDefault();
    questionTextRef.current?.focus();
    selectSpanContents(span);
  }

  /** Mission Design fill-in: any click or partial drag inside the placeholder selects all so typing replaces it. */
  function handleFillInSlotMouseUp() {
    const el = questionTextRef.current;
    if (!el) return;
    const span = el.querySelector(".gpt-fill-in-slot");
    if (!span) return;
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    if (!span.contains(sel.anchorNode)) return;
    selectSpanContents(span);
  }

  /** Keyboard partial selection (e.g. Shift+arrows) inside the placeholder → select all. */
  function handleFillInSlotKeyUp() {
    const el = questionTextRef.current;
    if (!el) return;
    const span = el.querySelector(".gpt-fill-in-slot");
    if (!span) return;
    const sel = window.getSelection();
    if (!sel.rangeCount || sel.isCollapsed) return;
    if (!span.contains(sel.anchorNode) || !span.contains(sel.focusNode)) return;
    selectSpanContents(span);
  }

  function reset() {
    setHasStarted(false);
    requestId.current += 1;
    setMessages([]);
    setUsedPrompts(new Set());
    try {
      localStorage.setItem(props.storageKey, JSON.stringify([]));
    } catch (e) {
      console.error("Failed to save messages to localStorage:", e);
    }
    if (btConfig) {
      setBudgetTableVisible(false);
      setBudgetValues({});
      setAiBudgetValues({});
      setReturnValues({});
      setRoiValues({});
      try { localStorage.removeItem(btConfig.storageKey); } catch {}
      try { localStorage.removeItem(btConfig.storageKey + "-ai"); } catch {}
      try { localStorage.removeItem(btConfig.storageKey + "-return"); } catch {}
      try { localStorage.removeItem(btConfig.storageKey + "-roi"); } catch {}
    }
    questionTextRef.current.focus();
  }

  function handleClearClick() {
    if (clearConfirm) {
      reset();
      setClearConfirm(false);
      return;
    }
    setClearConfirm(true);
    setTimeout(() => {
      setClearConfirm(false);
    }, 5000);
  }

  function limitMessagesByTokenCount(messages, tokenLimit) {
    const enc = encodingForModel("gpt-4");

    let totalTokens = 0;
    let processed = [];

    for (let i = messages.length - 1; i >= 0; i--) {
      const tokens = enc.encode(messages[i]["content"]);
      if (totalTokens + tokens.length > tokenLimit) {
        break;
      }
      totalTokens += tokens.length;
      processed.unshift(messages[i]);
    }

    console.log(totalTokens);

    return processed;
  }

  /** Strip HTML from prompt template strings (promptVal) for merging with budget fields on submit. */
  function htmlFragmentToPlainText(html) {
    if (!html || typeof html !== "string") return "";
    const d = document.createElement("div");
    d.innerHTML = html;
    return (d.textContent || "").replace(/\r\n/g, "\n").trim();
  }

  function promptConfigToPlainText(promptConfig) {
    if (!promptConfig?.promptVal?.length) return "";
    const v = promptConfig.promptVal;
    if (v.length === 1) return htmlFragmentToPlainText(v[0]);
    const mid =
      promptConfig.fillIn != null && promptConfig.fillIn !== "" ? String(promptConfig.fillIn) : "";
    return (htmlFragmentToPlainText(v[0]) + mid + htmlFragmentToPlainText(v[1] || "")).trim();
  }

  function getPromptText() {
    if (promptNum >= 0 && prompts[promptNum]?.budgetAllocation && budgetAllocationRef.current) {
      const prefix = promptConfigToPlainText(prompts[promptNum]);
      const body = budgetAllocationRef.current.getAssembledText();
      if (prefix && body) return `${prefix}\n\n${body}`;
      return body || prefix;
    }
    return getTextWithLineBreaks(questionTextRef);
  }

  function getTextWithLineBreaks(divRef) {
    if (!divRef.current) return "";

    let htmlContent = divRef.current.innerHTML;

    htmlContent = htmlContent.replace(/<div>/g, "\n").replace(/<\/div>/g, "");
    htmlContent = htmlContent.replace(/<p>/g, "\n").replace(/<\/p>/g, "");

    htmlContent = htmlContent.replace(/<br\s*\/?>/g, "\n");

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    return tempDiv.textContent;
  }

  function handleStopButton(e) {
    ignoreStreamId.current = currentStreamId.current;
    currentStreamId.current = "STOP_NOW";
    setIsStreaming(false);
  }

  function removeShowAfter(objectsArray) {
    return objectsArray.map((obj) => {
      const { showAfter, ...rest } = obj;
      return rest;
    });
  }

  function onSubmit(e) {
    e.preventDefault();

    if (isStreaming) {
      return;
    }

    if (promptNum >= 0 && prompts[promptNum]?.budgetAllocation && budgetAllocationRef.current) {
      if (!budgetAllocationRef.current.hasAllocationInput()) {
        return;
      }
    }

    let preCheckText = getPromptText();
    if (!preCheckText.trim()) {
      return;
    }

    const submittingPromptIndex = promptNum;

    const currentShowAfter = promptNum;
    let text = getPromptText();

    setHasStarted(true);
    setIsStreaming(true);
    setShowAfter(promptNum);
    setLastPromptRun(promptNum);

    const posterThreshold = props.workshopConfig.posterModeThreshold;
    if (posterThreshold != null && promptNum >= posterThreshold) {
      props.setPosterModeUnlocked?.(true);
      if (props.setDalleSourcePromptIndex) {
        props.setDalleSourcePromptIndex(promptNum);
      }
    }
    
    setUsedPrompts(prev => new Set([...prev, promptNum]));

    if (
      submittingPromptIndex >= 0 &&
      prompts[submittingPromptIndex]?.budgetAllocation &&
      budgetAllocationRef.current
    ) {
      const pwdKey = props.passwordStorageKey;
      const wid = props.workshopId;
      const sid = props.studentId;
      const pwd = pwdKey ? localStorage.getItem(pwdKey) : null;
      if (
        pwd &&
        wid &&
        String(wid) !== "unknown" &&
        sid != null &&
        sid !== -1
      ) {
        const total = budgetAllocationRef.current.getTotalPercent();
        if (Math.abs(total - 100) < 0.02) {
          const pct = budgetAllocationRef.current.getAllocationPercentages();
          fetch("/submit-mission-budget-allocation", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              password: pwd,
              workshop_id: String(wid),
              student_id: String(sid),
              prompt_index: submittingPromptIndex,
              power: pct.power,
              communication: pct.communication,
              launch: pct.launch,
              scientific: pct.scientific,
              navigation: pct.navigation,
            }),
          }).catch((err) => console.error("submit-mission-budget-allocation", err));
        }
      }
    }

    setPromptNum(-1);

    currentStreamId.current = null;

    let new_message = { role: "user", content: text };
    if (questionTextRef.current) {
      questionTextRef.current.innerHTML = "";
    }

    let token_limit = 4050;
    if (props.gptModel in TOKEN_LIMITS) {
      token_limit = TOKEN_LIMITS[props.gptModel];
    }
    console.log("token limit: " + token_limit);

    const message_limited = limitMessagesByTokenCount(
      [...removeShowAfter(messages), new_message],
      token_limit
    );

    console.log(message_limited);

    const useNewTokenParam = props.gptModel.startsWith("gpt-5") || props.gptModel.startsWith("gpt-4o");
    const tokenParam = useNewTokenParam
      ? { max_completion_tokens: 4096 }
      : { max_tokens: 4096 };

    let fetch_data = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + props.openaiApiKey,
      },
      payload: JSON.stringify({
        model: props.gptModel,
        stream: true,
        ...tokenParam,
        messages: message_limited,
      }),
    };

    askQuestion(fetch_data, new_message, 1, currentShowAfter);

    if (props.workshopConfig.scrollOutputIntoViewOnGo) {
      const scrollToLatestTurn = () => {
        lastSubmittedTurnRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToLatestTurn();
          setTimeout(scrollToLatestTurn, 100);
        });
      });
    }
  }

  function handleStreamError(e, attempt, fetch_data, new_message) {
    console.log("handleStreamError");

    let error_out = "";

    try {
      let error = JSON.parse(e.data);
      console.log(error);
      if ("error" in error && "message" in error["error"]) {
        error_out = error["error"]["message"];
      } else {
        error_out = error;
      }
    } catch {
      error_out = "failed to parse error data" + e;
    }

    console.log(`Stream error encountered. Attempt: ${attempt}`, error_out);

    const maxAttempts = 5;

    if (attempt < maxAttempts) {
      const jitter = Math.floor(Math.random() * 20);
      const delay = Math.pow(2, attempt) * 200 + jitter;

      console.log(`Retrying in ${delay}ms...`);

      setTimeout(() => {
        askQuestion(fetch_data, new_message, attempt + 1, showAfter);
      }, delay);
    } else {
      console.log("Maximum retry attempts reached. Aborting.");
      props.setErrorMessage(error_out);
      setIsStreaming(false);
    }
  }

  function handleStreamAbort(e) {
    console.log("stream abort");
    console.log(e);
  }

  function askQuestion(fetch_data, new_message, attempt, currentShowAfter) {
    var source = new SSE(
      "https://api.openai.com/v1/chat/completions",
      fetch_data
    );

    let newMessages = [
      ...messages,
      new_message,
      { role: "assistant", content: "", showAfter: currentShowAfter },
    ];
    console.log(newMessages);
    setMessages(newMessages);

    source.addEventListener("error", (e) =>
      handleStreamError(e, attempt, fetch_data, new_message)
    );
    source.addEventListener("abort", handleStreamAbort);

    source.addEventListener("message", function (e) {
      if (e.data != "[DONE]") {
        let result = JSON.parse(e.data);

        if (
          currentStreamId.current == null &&
          ignoreStreamId.current != result.id
        ) {
          currentStreamId.current = result.id;
        } else if (currentStreamId.current != result.id) {
          console.log("ignorning non-current id return!");
          return;
        }

        if (result?.choices?.[0]?.delta?.content) {
          const output = result.choices[0].delta.content;

          let newMessages2 = [...newMessages];
          newMessages2[newMessages2.length - 1]["content"] =
            newMessages2[newMessages2.length - 1]["content"] + output;
          setMessages(newMessages2);
        } else if (result && !result.choices) {
          props.setErrorMessage("Error: " + result);
          setIsStreaming(false);
        }
      } else {
        setIsStreaming(false);
        saveMesssagesToLocalStorage(newMessages);
        if (prompts[currentShowAfter]?.autoPopulateDalle && props.setDallePromptText) {
          const lastMsg = newMessages[newMessages.length - 1];
          if (lastMsg && lastMsg.role === "assistant" && lastMsg.content) {
            props.setDallePromptText(lastMsg.content);
            if (props.setDalleSourcePromptIndex) {
              props.setDalleSourcePromptIndex(currentShowAfter);
            }
          }
        }
      }
    });

    console.log("starting stream");
    source.stream();
  }
}
export { Gpt };
